<template>
  <div :class="containerClass">
    <table class="table b-table" :class="tableClasses" v-bind="$attrs">
      <thead>
        <tr>
          <th
            v-for="field in fields"
            :key="field.key"
            @click="field.sortable !== false ? sort(field.key) : null"
            :class="[
              field.thClass,
              field.class,
              { 'cursor-pointer': field.sortable !== false },
            ]"
            :style="field.thStyle"
          >
            <slot
              :name="`head(${field.key})`"
              :field="field"
              :label="field.label !== undefined ? field.label : field.key"
            >
              {{ field.label !== undefined ? field.label : field.key }}
              <span v-if="field.sortable !== false && currentSortBy === field.key">
                {{ currentSortDesc ? "▼" : "▲" }}
              </span>
            </slot>
          </th>
        </tr>
      </thead>
      <tbody v-if="!busy">
        <tr
          v-for="(item, index) in sortedItems"
          :key="index"
          :class="getRowClass(item, index)"
          @click="onRowClick(item, index, $event)"
        >
          <td
            v-for="field in fields"
            :key="field.key"
            :class="[field.tdClass, field.class]"
            :style="field.tdStyle"
          >
            <slot
              :name="`cell(${field.key})`"
              :data="{ item, index, value: item[field.key] }"
              :item="item"
              :index="index"
              :value="item[field.key]"
            >
              {{ item[field.key] }}
            </slot>
          </td>
        </tr>
        <tr v-if="sortedItems.length === 0">
          <td :colspan="fields.length" class="text-center">
            <slot name="empty">No data available</slot>
          </td>
        </tr>
      </tbody>
      <tbody v-else>
        <tr>
          <td :colspan="fields.length" class="text-center">
            <div class="spinner-border" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
export default {
  name: "BaseTable",
  inheritAttrs: false,
  props: {
    items: {
      type: Array,
      required: true,
    },
    fields: {
      type: Array,
      required: true,
    },
    striped: {
      type: Boolean,
      default: false,
    },
    hover: {
      type: Boolean,
      default: false,
    },
    responsive: {
      type: Boolean,
      default: false,
    },
    bordered: {
      type: Boolean,
      default: false,
    },
    small: {
      type: Boolean,
      default: false,
    },
    busy: {
      type: Boolean,
      default: false,
    },
    sortBy: {
      type: String,
      default: "",
    },
    sortDesc: {
      type: Boolean,
      default: false,
    },
    noLocalSorting: {
      type: Boolean,
      default: false,
    },
    tbodyTrClass: {
      type: [String, Function],
      default: "",
    },
  },
  emits: ["sort-changed", "row-clicked"],
  data() {
    return {
      currentSortBy: this.sortBy,
      currentSortDesc: this.sortDesc,
    };
  },
  computed: {
    containerClass() {
      return this.responsive ? "table-responsive" : "table-container";
    },
    tableClasses() {
      const classes = [];
      if (this.striped) classes.push("table-striped");
      if (this.hover) classes.push("table-hover");
      if (this.bordered) classes.push("table-bordered");
      if (this.small) classes.push("table-sm");
      return classes.join(" ");
    },
    sortedItems() {
      if (this.noLocalSorting || !this.currentSortBy) {
        return this.items;
      }

      const sorted = [...this.items].sort((a, b) => {
        const aVal = a[this.currentSortBy];
        const bVal = b[this.currentSortBy];

        if (aVal === bVal) return 0;
        if (aVal === null || aVal === undefined) return 1;
        if (bVal === null || bVal === undefined) return -1;

        if (typeof aVal === "string") {
          return aVal.localeCompare(bVal);
        }

        return aVal < bVal ? -1 : 1;
      });

      return this.currentSortDesc ? sorted.reverse() : sorted;
    },
  },
  watch: {
    sortBy(newVal) {
      this.currentSortBy = newVal;
    },
    sortDesc(newVal) {
      this.currentSortDesc = newVal;
    },
  },
  methods: {
    sort(key) {
      if (this.currentSortBy === key) {
        this.currentSortDesc = !this.currentSortDesc;
      } else {
        this.currentSortBy = key;
        this.currentSortDesc = false;
      }
      this.$emit("sort-changed", {
        sortBy: this.currentSortBy,
        sortDesc: this.currentSortDesc,
      });
    },
    getRowClass(item, index) {
      if (typeof this.tbodyTrClass === "function") {
        return this.tbodyTrClass(item, index);
      }
      return this.tbodyTrClass;
    },
    onRowClick(item, index, event) {
      this.$emit("row-clicked", item, index, event);
    },
  },
};
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
  user-select: none;
}

.table-container {
  overflow: visible;
  position: relative;
}

.table tbody tr:not(:last-child) td {
  border-bottom: 1px solid #dee2e6;
}

@media (max-width: 768px) {
  .table-container {
    overflow-x: auto;
  }
}
</style>
